import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { z } from "zod";
import { sessionStore } from "../../../lib/store";
import { trackEmailVisitEngagement } from "../../../tools/analytics/base.analytics";

/**
 * A visit ends after this long without foreground time: the next arrival in
 * the same tab is a new visit, not the tail of the email one.
 */
const VISIT_IDLE_END_MS = 30 * 60 * 1000;

/**
 * A tab left open in the foreground is not a reader. One stretch counts at
 * most this long.
 */
const MAX_STRETCH_MS = 30 * 60 * 1000;

const emailVisitSchema = z.object({
  visitId: z.string(),
  utmSource: z.string(),
  utmContent: z.string().nullable(),
  lastActiveAt: z.number(),
});

// The campaign an email link carries, read off the URL.
const emailArrivalSchema = z.object({
  medium: z.literal("email"),
  source: z.string().min(1),
  content: z.string().min(1).nullable(),
});

// The tab's visit, in session storage so a reload inside the visit keeps it.
const emailVisitStore = sessionStore(
  "email-visit",
  null,
  emailVisitSchema.nullable()
);

/**
 * Measures the foreground time of a visit that started from one of our emails:
 * every Juisci email link carries `utm_medium=email` and the workflow in
 * `utm_source`. Each time the tab is hidden or closed, the stretch since it
 * was last shown is sent (`trackEmailVisitEngagement`).
 *
 * A router-level hook beside `useReportEmailClick`, for the reason given
 * there: business components hold no effect. Unlike the click, it needs no
 * session: a reader who has not signed in is measured too. A mail scanner
 * that rewrites the link loses `utm_medium=email` and is not measured.
 */
export function useEmailVisitEngagement(): void {
  const location = useLocation();
  // When the current foreground stretch began; null while the tab is hidden.
  // Shared by the two effects: a new visit closes the stretch of the old one.
  const shownAt = useRef<number | null>(null);

  // A tab in the foreground is reading now, whatever `lastActiveAt` says.
  function isIdle(
    visit: z.infer<typeof emailVisitSchema>,
    now: number
  ): boolean {
    return (
      shownAt.current === null && now - visit.lastActiveAt > VISIT_IDLE_END_MS
    );
  }

  function sendStretch(now: number): void {
    const start = shownAt.current;
    if (start === null) return;
    shownAt.current = null;
    const visit = emailVisitStore.get();
    if (visit === null) return;
    emailVisitStore.set({ ...visit, lastActiveAt: now });
    trackEmailVisitEngagement({
      visitId: visit.visitId,
      utmSource: visit.utmSource,
      utmContent: visit.utmContent,
      durationSeconds: Math.round(Math.min(now - start, MAX_STRETCH_MS) / 1000),
    });
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Most arrivals are not from an email: a failed parse is that case, not
    // corrupt input.
    const arrival = emailArrivalSchema.safeParse({
      medium: params.get("utm_medium"),
      source: params.get("utm_source"),
      content: params.get("utm_content"),
    });
    if (!arrival.success) return;
    const utmSource = arrival.data.source;
    const utmContent = arrival.data.content;

    // A reload or a Back to the email link inside the visit keeps it.
    const now = Date.now();
    const visit = emailVisitStore.get();
    if (
      visit !== null &&
      visit.utmSource === utmSource &&
      visit.utmContent === utmContent &&
      !isIdle(visit, now)
    )
      return;
    // The time before this arrival belongs to the previous visit, if any.
    sendStretch(now);
    emailVisitStore.set({
      visitId: crypto.randomUUID(),
      utmSource,
      utmContent,
      lastActiveAt: now,
    });
    if (document.visibilityState === "visible") shownAt.current = now;
  }, [location.search]);

  useEffect(() => {
    const visit = emailVisitStore.get();
    if (visit !== null && isIdle(visit, Date.now())) emailVisitStore.set(null);
    if (shownAt.current === null && document.visibilityState === "visible")
      shownAt.current = Date.now();

    function startStretch(now: number): void {
      const current = emailVisitStore.get();
      if (current !== null && isIdle(current, now)) emailVisitStore.set(null);
      shownAt.current = now;
    }

    function handleVisibilityChange(): void {
      const now = Date.now();
      if (document.visibilityState === "hidden") {
        sendStretch(now);
        return;
      }
      startStretch(now);
    }

    function handlePageHide(): void {
      sendStretch(Date.now());
    }

    // A page restored from the back-forward cache fires `pageshow`, and Safari
    // often skips `visibilitychange` on the way: without this the clock that
    // `pagehide` stopped would stay stopped.
    function handlePageShow(event: PageTransitionEvent): void {
      if (!event.persisted) return;
      if (document.visibilityState !== "visible") return;
      if (shownAt.current !== null) return;
      startStretch(Date.now());
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);
}
